import React, { useEffect, useState } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { Package, Truck, Clock, CheckCircle, AlertTriangle, ShoppingBag, MapPin, Star } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../hooks/useAuth';
import { Order, OrderStatus } from '../types';
import { Button } from './Button';

const StatusBadge: React.FC<{ status: OrderStatus }> = ({ status }) => {
  const styles = {
    'Just Ordered': 'bg-blue-50 text-blue-600 border-blue-100',
    'Dispatched': 'bg-orange-50 text-orange-600 border-orange-100',
    'Arriving': 'bg-purple-50 text-purple-600 border-purple-100',
    'Delivered': 'bg-green-50 text-green-600 border-green-100',
  };

  const icons = {
    'Just Ordered': <Clock size={10} />,
    'Dispatched': <Truck size={10} />,
    'Arriving': <Clock size={10} />,
    'Delivered': <CheckCircle size={10} />,
  };

  return (
    <span className={`px-2.5 py-1 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-1.5 border shadow-sm ${styles[status]}`}>
      {icons[status]} {status}
    </span>
  );
};

export const OrdersPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.mobile) return;

    const fetchOrders = () => {
      setLoading(true);
      setError(null);

      const q = query(
        collection(db, "orders"),
        where("userId", "==", user.mobile)
      );

      const unsubscribe = onSnapshot(q, (snapshot) => {
        const ordersData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Order[];
        
        ordersData.sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA;
        });

        setOrders(ordersData);
        setLoading(false);
      }, (err) => {
        console.error("Orders fetch error:", err);
        setError("Failed to connect to order database.");
        setLoading(false);
      });

      return unsubscribe;
    };

    const unsubscribe = fetchOrders();
    return () => unsubscribe?.();
  }, [user]);

  if (!user) {
    return (
      <div className="container mx-auto px-4 py-20 text-center flex flex-col items-center justify-center min-h-[60vh]">
        <AlertTriangle className="text-orange-400 mb-4" size={48} />
        <h2 className="text-lg font-serif mb-4 text-stone-900 uppercase tracking-widest font-black">Sign in to see your orders</h2>
        <Button onClick={() => navigate('/')} className="rounded-2xl">Return Home</Button>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-20 flex flex-col items-center justify-center min-h-[60vh]">
        <div className="relative w-16 h-16">
          <div className="absolute inset-0 border-4 border-orange-500/10 rounded-full"></div>
          <div className="absolute inset-0 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <p className="mt-4 text-[10px] font-black tracking-widest text-stone-500 uppercase">Loading orders...</p>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl min-h-screen">
      <div className="flex items-center justify-between mb-8 border-b border-orange-100 pb-6">
        <h1 className="text-xl font-serif font-black text-stone-900 flex items-center gap-3 tracking-tighter uppercase">
          <Package className="text-orange-600" /> My Orders
        </h1>
        <div className="text-right">
          <p className="text-[9px] uppercase font-black text-stone-500 tracking-[0.2em]">Customer</p>
          <p className="text-[10px] font-black text-stone-900 uppercase">{user.fullName || user.name}</p>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-3xl border border-orange-100 shadow-sm animate-[scaleIn_0.3s_ease-out]">
          <ShoppingBag className="mx-auto text-stone-300 mb-6" size={64} />
          <h2 className="text-lg font-serif font-black text-stone-900 mb-2 uppercase">No Orders Found</h2>
          <p className="text-[10px] text-stone-500 mb-8 max-w-xs mx-auto uppercase font-bold tracking-wider">Your order history is empty. Start shopping to see your orders here.</p>
          <Button onClick={() => navigate('/')} size="lg" className="rounded-2xl">Start Shopping</Button>
        </div>
      ) : (
        <div className="space-y-6">
          {orders.map((order) => (
            <div key={order.id} className="bg-white rounded-3xl shadow-xl shadow-orange-100/20 border border-orange-100 overflow-hidden hover:border-orange-300 transition-all group">
              <div className="p-4 bg-orange-50/50 border-b border-orange-100 flex flex-wrap justify-between items-center gap-4">
                <div className="space-y-1">
                  <p className="text-[8px] uppercase font-black text-stone-400 tracking-widest">Order ID</p>
                  <p className="text-[10px] font-mono text-stone-900 font-bold">{order.id.substring(0, 12).toUpperCase()}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-[8px] uppercase font-black text-stone-400 tracking-widest">Date</p>
                  <p className="text-[10px] text-stone-900 font-bold">
                    {new Date(order.createdAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }).toUpperCase()}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-[8px] uppercase font-black text-stone-400 tracking-widest">Status</p>
                  <StatusBadge status={order.status} />
                </div>
                <div className="space-y-1 text-right">
                  <p className="text-[8px] uppercase font-black text-stone-400 tracking-widest">Total</p>
                  <p className="text-[14px] font-black text-orange-700 tracking-tight">₹{order.totalAmount.toLocaleString('en-IN')}</p>
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <p className="text-[9px] uppercase font-black tracking-[0.2em] text-stone-400">Products</p>
                    <div className="space-y-3">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-4 p-2 rounded-2xl hover:bg-orange-50/50 transition-colors cursor-pointer" onClick={() => navigate(`/product/${item.id}`)}>
                          <div className="w-14 h-14 bg-white rounded-xl overflow-hidden flex-shrink-0 border border-orange-100 shadow-sm group-hover:scale-105 transition-transform">
                            {item.images && item.images.length > 0 ? (
                              <img src={item.images[0]} className="w-full h-full object-cover" alt={item.name} />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-orange-50 text-orange-200">
                                <ShoppingBag size={16} />
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[11px] font-black text-stone-900 truncate uppercase leading-tight mb-1">{item.name}</p>
                            <div className="flex items-center gap-2">
                               <span className="text-[9px] text-orange-600 font-black uppercase bg-orange-50 px-1.5 py-0.5 rounded-lg border border-orange-100">Qty: {item.quantity}</span>
                               <span className="text-[9px] text-stone-400 font-bold uppercase tracking-widest">₹{(item.unitPrice || item.currentPrice || 0).toLocaleString()}</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-4">
                    <p className="text-[9px] uppercase font-black tracking-[0.2em] text-stone-400">Shipping Address</p>
                    <div className="text-[10px] bg-orange-50/30 p-5 rounded-3xl border border-orange-100 space-y-3 font-bold uppercase leading-relaxed ring-4 ring-orange-50/50">
                      <div className="flex items-center gap-2 text-stone-900 border-b border-orange-100/50 pb-2">
                        <MapPin size={14} className="text-orange-600" />
                        <span className="font-black text-stone-900">{order.shipping.fullName}</span>
                      </div>
                      <p className="text-stone-600 leading-relaxed font-medium">
                        {order.shipping.address}<br />
                        <span className="text-orange-700">{order.shipping.district}, {order.shipping.state}</span><br />
                        <span className="inline-block mt-2 bg-orange-600 text-white px-2 py-0.5 rounded-lg shadow-sm">PIN: {order.shipping.pincode}</span>
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="mt-8 flex flex-col md:flex-row items-center gap-4">
                  <div className="flex-1 w-full p-4 bg-orange-50/50 border border-orange-100 rounded-2xl flex items-start gap-4">
                    <div className="w-10 h-10 bg-white text-orange-600 rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm border border-orange-100">
                      <Clock size={20} />
                    </div>
                    <div className="flex-1">
                      <p className="text-[8px] font-black uppercase text-stone-500 tracking-[0.2em] mb-1">Delivery Update</p>
                      <div className="text-[11px] font-medium uppercase tracking-tight">
                        {order.status === 'Delivered' ? (
                          <span className="text-green-600 font-black flex items-center gap-1.5"><CheckCircle size={12} /> Successfully Delivered</span>
                        ) : order.arrivingDate ? (
                          <p>Expected arrival: <span className="text-orange-700 font-black">{new Date(order.arrivingDate).toLocaleDateString().toUpperCase()}</span></p>
                        ) : (
                          <p className="italic text-stone-500">Preparing for dispatch.</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {order.status === 'Delivered' && (
                    <Button 
                      onClick={() => navigate(`/product/${order.items[0].id}`)}
                      className="w-full md:w-auto px-8 py-4 rounded-2xl bg-stone-900 text-white font-black uppercase tracking-widest text-[10px] shadow-xl shadow-stone-200 hover:bg-stone-800 transition-all flex items-center justify-center gap-2"
                    >
                      <Star size={14} className="fill-orange-500 text-orange-500" /> Write Review
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};